import React, { useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, A11y } from "swiper";
import { BaseModal } from "../modals/base-modal";
import { useLanguage } from "../../hooks/use-language";
import { AVAILABLE_LANGUAGES } from "../../config/constants";
import { useId } from "../../hooks/use-id";

/**
 * Resuelve un valor i18n simple usando el idioma actual y un fallback a español o inglés.
 * @param {{value:string | {es?:string, en?:string}, lang:"es"|"en"}} options
 * @returns {string} valor localizado
 */
const resolveI18nValue = ({ value, lang }) => {
	if (typeof value === "string") return value;
	if (!value || typeof value !== "object") return "";
	return value?.[lang] || value?.[AVAILABLE_LANGUAGES.es] || value?.[AVAILABLE_LANGUAGES.en] || "";
};

/**
 * Modal reutilizable para tutoriales de cuenta personal.
 * Renderiza una secuencia de pasos usando Swiper y permite cerrar el modal
 * desde botón, tecla Escape o click sobre el overlay.
 *
 * @param {Object} props
 * @param {boolean} props.visible - indica si el modal está visible
 * @param {function} props.setVisible - cambia la visibilidad del modal
 * @param {function} [props.onClose] - callback de cierre personalizado
 * @param {Array<{title:string|Object, description:string|Object, imageUrl:string|Object}>} props.steps - pasos del tutorial
 * @param {string} [props.modalId] - id base para atributos accesibles
 * @returns {JSX.Element}
 */
export const TutorialModal = ({ visible, setVisible, onClose, steps = [], modalId = "personal-account-tutorial-modal" }) => {
	const lang = useLanguage();
	const titleId = useId(`${modalId}-title`);
	const descriptionId = useId(`${modalId}-description`);
	const prevButtonRef = useRef(null);
	const nextButtonRef = useRef(null);
	const [activeIndex, setActiveIndex] = useState(0);

	const localizedSteps = useMemo(() => {
		return steps.map((step) => ({
			title: resolveI18nValue({ value: step?.title, lang }),
			description: resolveI18nValue({ value: step?.description, lang }),
			imageUrl: resolveI18nValue({ value: step?.imageUrl, lang }),
		}));
	}, [lang, steps]);

	const activeStep = localizedSteps?.[activeIndex] || {};
	const totalSteps = localizedSteps.length;
	const closeLabel = lang === AVAILABLE_LANGUAGES.en ? "Close tutorial" : "Cerrar tutorial";
	const previousLabel = lang === AVAILABLE_LANGUAGES.en ? "Previous step" : "Paso anterior";
	const nextLabel = lang === AVAILABLE_LANGUAGES.en ? "Next step" : "Siguiente paso";

	/**
	 * Conecta los botones personalizados de navegación con la instancia de Swiper.
	 * @param {import("swiper").Swiper} swiper
	 */
	const updateNavigation = (swiper) => {
		swiper.params.navigation.prevEl = prevButtonRef?.current;
		swiper.params.navigation.nextEl = nextButtonRef?.current;
		swiper.navigation.destroy();
		swiper.navigation.init();
		swiper.navigation.update();
	};

	/**
	 * Cierra el modal usando el callback externo si existe.
	 */
	const close = () => {
		if (onClose) {
			onClose();
			return;
		}
		setVisible && setVisible(false);
	};

	return (
		<BaseModal
			visible={visible}
			setVisible={onClose || setVisible}
			backdropClose={true}
			overlayClassName={"personal-account-tutorials__overlay"}
			windowClassName={"personal-account-tutorials__window"}
			ariaLabelledBy={titleId}
			ariaDescribedBy={descriptionId}
		>
			<div className="personal-account-tutorial">
				<div className="personal-account-tutorial__header">
					<button className="personal-account-tutorial__close focusable-primary" aria-label={closeLabel} onClick={close}>
						<Icon icon="mdi:close-thick" width={28} />
					</button>
				</div>
				<div className="personal-account-tutorial__body">
					<h2 id={titleId} className="personal-account-tutorial__title  heading-2">
						{activeStep?.title}
					</h2>
					<p
						id={descriptionId}
						className="personal-account-tutorial__description body-l"
						dangerouslySetInnerHTML={{ __html: activeStep?.description || "" }}
					/>
					<Swiper
						className="personal-account-tutorial__swiper"
						slidesPerView={1}
						navigation={{
							nextEl: nextButtonRef?.current,
							prevEl: prevButtonRef?.current,
						}}
						onSwiper={updateNavigation}
						onSlideChange={(swiper) => {
							setActiveIndex(swiper.realIndex || 0);
						}}
						keyboard={{ enabled: true }}
						modules={[Navigation, Keyboard, A11y]}
						a11y={{
							containerMessage: lang === AVAILABLE_LANGUAGES.en ? "Profile tutorial" : "Tutorial del perfil",
							itemRoleDescriptionMessage: lang === AVAILABLE_LANGUAGES.en ? "Tutorial step" : "Paso del tutorial",
						}}
					>
						{localizedSteps.map((step, index) => (
							<SwiperSlide key={`${modalId}-step-${index + 1}`}>
								<div className="personal-account-tutorial__slide">
									<img
										className="personal-account-tutorial__image"
										src={step?.imageUrl}
										alt={step?.title}
									/>
								</div>
							</SwiperSlide>
						))}
					</Swiper>
				</div>
				<div className="personal-account-tutorial__footer">
					<button
						ref={prevButtonRef}
						type="button"
						aria-label={previousLabel}
						className="personal-account-tutorial__nav personal-account-tutorial__nav--prev focusable-red"
					>
						<Icon icon="mdi:arrow-left" width={28} />
					</button>
					<div className="personal-account-tutorial__counter secondary-font" aria-live="polite">
						{`${activeIndex + 1}/${totalSteps}`}
					</div>
					<button
						ref={nextButtonRef}
						type="button"
						aria-label={nextLabel}
						className="personal-account-tutorial__nav personal-account-tutorial__nav--next focusable-red"
					>
						<Icon icon="mdi:arrow-right" width={28} />
					</button>
				</div>
			</div>
		</BaseModal>
	);
};
